import axios from "axios";
import type { BackendAuthResponse } from "./types";

const authHttpClient = axios.create({
  baseURL: process.env.API_URL,
  timeout: 30000,
});

async function fetchBackendSession(middletierToken: string): Promise<BackendAuthResponse> {
  const { data } = await authHttpClient.get<BackendAuthResponse>("/auth/excel", {
    headers: {
      Authorization: `Bearer ${middletierToken}`,
      "Cache-Control": "no-cache",
    },
  });

  return data;
}

async function getOfficeTokenSilently(): Promise<string> {
  return OfficeRuntime.auth.getAccessToken({
    allowSignInPrompt: false,
    allowConsentPrompt: false,
  });
}

async function getOfficeTokenInteractively(): Promise<string> {
  return OfficeRuntime.auth.getAccessToken({
    allowSignInPrompt: true,
    allowConsentPrompt: true,
  });
}

export const authProvider = {
  async signIn(): Promise<string> {
    let officeToken: string;

    try {
      officeToken = await getOfficeTokenSilently();
    } catch {
      officeToken = await getOfficeTokenInteractively();
    }

    let response = await fetchBackendSession(officeToken);

    if (response.claims) {
      const challengedToken = await OfficeRuntime.auth.getAccessToken({
        authChallenge: response.claims,
      });
      response = await fetchBackendSession(challengedToken);
    }

    if (response.error) {
      throw new Error(response.error_description || response.error);
    }

    if (!response.token) {
      throw new Error("Backend token missing");
    }

    return response.token;
  },

  async refreshSilently(): Promise<string> {
    const officeToken = await getOfficeTokenSilently();
    let response = await fetchBackendSession(officeToken);

    if (response.claims) {
      const challengedToken = await OfficeRuntime.auth.getAccessToken({
        authChallenge: response.claims,
      });
      response = await fetchBackendSession(challengedToken);
    }

    if (response.error || !response.token) {
      throw new Error(response.error_description || response.error || "Refresh failed");
    }

    return response.token;
  },
};